import { useCallback, useEffect, useRef, useState } from "react";
import { Plus, Pencil, Trash2, Upload, Download, Search, Eye } from "lucide-react";
import { toast } from "sonner";
import { api, downloadText, errMsg, fileUrl, METHODS } from "@/lib/api";
import { Empty, NativeSelect, Spinner } from "@/components/Brand";
import { Confirm } from "@/components/Confirm";
import { ImageUpload } from "@/components/ImageUpload";
import { GoldBtn, PageTitle } from "@/pages/admin/AdminLayout";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";

const KEYS = ["A", "B", "C", "D", "E"];
const newQ = (subject_id) => ({ subject_id, subtopic: "", difficulty: "sedang", text: "", image: null, answer_key: "A", explanation: "", explanation_image: null, active: true, package_ids: [],
  options: KEYS.map((k) => ({ key: k, text: "", image: null, score: 0 })) });

const Preview = ({ q, method }) => (
  <div className="space-y-3 rounded-lg border bg-slate-50 p-4" data-testid="question-preview">
    {q.text && <p className="whitespace-pre-line">{q.text}</p>}
    {q.image && <img src={fileUrl(q.image)} alt="" className="max-h-60 max-w-full rounded border object-contain" />}
    {q.options.map((o) => (
      <div key={o.key} className={`flex items-start gap-3 rounded border bg-white p-3 ${method === "benar_salah" && q.answer_key === o.key ? "border-emerald-500" : ""}`}>
        <b>{o.key}</b><div className="flex-1">{o.text}{o.image && <img src={fileUrl(o.image)} alt="" className="mt-1 max-h-24 rounded border" />}</div>
        {method === "skor_pilihan" && <span className="text-xs font-mono">skor {o.score}</span>}
      </div>
    ))}
    {q.explanation && <p className="rounded bg-amber-50 p-3 text-sm"><b>Pembahasan:</b> {q.explanation}</p>}
    {q.explanation_image && <img src={fileUrl(q.explanation_image)} alt="" className="max-h-48 rounded border" />}
  </div>
);

const QuestionDialog = ({ q, setQ, subjects, packages, onSaved }) => {
  const subject = subjects.find((s) => s.id === q.subject_id);
  const method = subject?.scoring_method || "benar_salah";
  const pkgOpts = packages.filter((p) => p.category_id === subject?.category_id);
  const nOpt = q.options.length;
  const setOpt = (i, patch) => setQ({ ...q, options: q.options.map((o, j) => (j === i ? { ...o, ...patch } : o)) });
  const setCount = (n) => {
    const opts = KEYS.slice(0, n).map((k, i) => q.options[i] || { key: k, text: "", image: null, score: 0 });
    setQ({ ...q, options: opts, answer_key: KEYS.slice(0, n).includes(q.answer_key) ? q.answer_key : "A" });
  };
  const save = async () => {
    const body = { ...q, options: q.options.map((o) => ({ ...o, score: Number(o.score) || 0 })), answer_key: method === "benar_salah" ? q.answer_key : null };
    try {
      if (q.id) await api.put(`/admin/questions/${q.id}`, body);
      else await api.post("/admin/questions", body);
      toast.success("Soal disimpan");
      onSaved();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Tabs defaultValue="edit">
      <TabsList><TabsTrigger value="edit" data-testid="qform-tab-edit">Isi Soal</TabsTrigger><TabsTrigger value="preview" data-testid="qform-tab-preview"><Eye className="mr-1 h-4 w-4" />Pratinjau</TabsTrigger></TabsList>
      <TabsContent value="edit" className="space-y-4 pt-3">
        <div className="grid gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2"><Label>Materi</Label><NativeSelect value={q.subject_id} onChange={(e) => setQ({ ...q, subject_id: e.target.value, package_ids: [] })} data-testid="qform-subject-select">
            {subjects.map((s) => <option key={s.id} value={s.id}>{s.category_name} — {s.name}</option>)}</NativeSelect></div>
          <div><Label>Kesulitan</Label><NativeSelect value={q.difficulty} onChange={(e) => setQ({ ...q, difficulty: e.target.value })} data-testid="qform-difficulty-select"><option value="mudah">Mudah</option><option value="sedang">Sedang</option><option value="sulit">Sulit</option></NativeSelect></div>
          <div><Label>Submateri</Label><Input value={q.subtopic} onChange={(e) => setQ({ ...q, subtopic: e.target.value })} data-testid="qform-subtopic-input" /></div>
          <div><Label>Jumlah pilihan</Label><NativeSelect value={nOpt} onChange={(e) => setCount(Number(e.target.value))} data-testid="qform-optcount-select">{[2, 3, 4, 5].map((n) => <option key={n} value={n}>{n} (A–{KEYS[n - 1]})</option>)}</NativeSelect></div>
          <div className="flex items-end gap-2 pb-2"><Switch checked={q.active} onCheckedChange={(v) => setQ({ ...q, active: v })} data-testid="qform-active-switch" /><Label>Aktif</Label></div>
        </div>
        <p className="text-xs font-semibold text-amber-700">Metode materi ini: {METHODS[method]}{method === "skor_pilihan" ? " — isi skor tiap pilihan (boleh 0)." : " — pilih kunci jawaban."}</p>
        <div><Label>Teks pertanyaan</Label><Textarea rows={4} value={q.text} onChange={(e) => setQ({ ...q, text: e.target.value })} data-testid="qform-text-input" /></div>
        <ImageUpload value={q.image} onChange={(v) => setQ({ ...q, image: v })} testid="qform-question-image" label="gambar pertanyaan" />
        <div className="space-y-3">
          {q.options.map((o, i) => (
            <div key={o.key} className="rounded-lg border p-3" data-testid={`qform-option-${o.key}`}>
              <div className="flex flex-wrap items-center gap-3">
                <span className="flex h-8 w-8 items-center justify-center rounded-full bg-[#0A192F] font-bold text-amber-300">{o.key}</span>
                <Input className="min-w-[200px] flex-1" placeholder={`Teks pilihan ${o.key}`} value={o.text} onChange={(e) => setOpt(i, { text: e.target.value })} data-testid={`qform-option-${o.key}-text`} />
                {method === "skor_pilihan"
                  ? <div className="flex items-center gap-1"><Label className="text-xs">Skor</Label><Input type="number" step="any" className="w-20" value={o.score} onChange={(e) => setOpt(i, { score: e.target.value })} data-testid={`qform-option-${o.key}-score`} /></div>
                  : <label className="flex items-center gap-1 text-xs font-semibold"><input type="radio" name="answer_key" checked={q.answer_key === o.key} onChange={() => setQ({ ...q, answer_key: o.key })} data-testid={`qform-answer-${o.key}`} />Kunci</label>}
              </div>
              <div className="mt-2 pl-11"><ImageUpload compact value={o.image} onChange={(v) => setOpt(i, { image: v })} testid={`qform-option-${o.key}-image`} label={`gambar ${o.key}`} /></div>
            </div>
          ))}
        </div>
        <div><Label>Pembahasan</Label><Textarea rows={3} value={q.explanation} onChange={(e) => setQ({ ...q, explanation: e.target.value })} data-testid="qform-explanation-input" /></div>
        <ImageUpload value={q.explanation_image} onChange={(v) => setQ({ ...q, explanation_image: v })} testid="qform-explanation-image" label="gambar pembahasan" />
        {pkgOpts.length > 0 && (
          <div><Label>Gunakan di paket tryout</Label>
            <div className="mt-2 flex flex-wrap gap-3">{pkgOpts.map((p) => (
              <label key={p.id} className="flex items-center gap-2 rounded border px-3 py-1.5 text-sm">
                <input type="checkbox" checked={q.package_ids.includes(p.id)} onChange={(e) => setQ({ ...q, package_ids: e.target.checked ? [...q.package_ids, p.id] : q.package_ids.filter((x) => x !== p.id) })} data-testid={`qform-package-${p.id}`} />{p.name}
              </label>
            ))}</div>
          </div>
        )}
      </TabsContent>
      <TabsContent value="preview" className="pt-3"><Preview q={q} method={method} /></TabsContent>
      <GoldBtn onClick={save} className="mt-4 w-full justify-center py-2.5" data-testid="qform-save-btn">Simpan Soal</GoldBtn>
    </Tabs>
  );
};

const ImportDialog = ({ subjects, onDone }) => {
  const [sid, setSid] = useState(subjects[0]?.id || "");
  const ref = useRef(null);
  const run = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f || !sid) return;
    const fd = new FormData();
    fd.append("subject_id", sid);
    fd.append("file", f);
    try {
      const { data } = await api.post("/admin/questions/import", fd);
      toast.success(`${data.imported} soal berhasil diimpor`);
      if (data.errors.length) toast.warning(data.errors.slice(0, 5).join("\n"), { duration: 10000 });
      onDone();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };
  const tpl = "pertanyaan,a,b,c,d,e,kunci,skor_a,skor_b,skor_c,skor_d,skor_e,pembahasan,kesulitan,submateri\nIbu kota Indonesia adalah?,Jakarta,Bandung,Surabaya,Medan,Makassar,A,,,,,,Ibu kota negara,mudah,Geografi\n";
  return (
    <div className="space-y-4">
      <p className="text-sm text-slate-600">Kolom <b>kunci</b> wajib untuk materi Benar/Salah. Kolom <b>skor_a … skor_e</b> dipakai untuk materi Skor per pilihan. Gambar dapat ditambahkan setelah impor melalui tombol edit.</p>
      <div><Label>Materi tujuan</Label><NativeSelect value={sid} onChange={(e) => setSid(e.target.value)} data-testid="import-subject-select">{subjects.map((s) => <option key={s.id} value={s.id}>{s.category_name} — {s.name}</option>)}</NativeSelect></div>
      <div className="flex gap-2">
        <button onClick={() => downloadText(tpl, "template-soal.csv")} className="inline-flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-semibold" data-testid="import-template-btn"><Download className="h-4 w-4" />Template CSV</button>
        <input ref={ref} type="file" accept=".csv,.xlsx" className="hidden" onChange={run} data-testid="import-file-input" />
        <GoldBtn onClick={() => ref.current?.click()} data-testid="import-choose-btn"><Upload className="h-4 w-4" />Pilih file CSV/Excel</GoldBtn>
      </div>
    </div>
  );
};

export default function QuestionsAdmin() {
  const [cats, setCats] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [packages, setPackages] = useState([]);
  const [flt, setFlt] = useState({ category_id: "", subject_id: "", difficulty: "", q: "", active: "", package_id: "" });
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [edit, setEdit] = useState(null);
  const [importOpen, setImportOpen] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/categories", { params: { all: true } }), api.get("/subjects", { params: { all: true } }), api.get("/admin/packages")]).then(([c, s, p]) => {
      const cm = Object.fromEntries(c.data.map((x) => [x.id, x.name]));
      setCats(c.data);
      setSubjects(s.data.map((x) => ({ ...x, category_name: cm[x.category_id] })));
      setPackages(p.data);
    }).catch((e) => toast.error(errMsg(e)));
  }, []);
  const load = useCallback(() => {
    const params = { page, limit: 20 };
    Object.entries(flt).forEach(([k, v]) => { if (v !== "") params[k] = v; });
    api.get("/admin/questions", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [flt, page]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);

  const setF = (k) => (e) => { setPage(1); setFlt({ ...flt, [k]: e.target.value, ...(k === "category_id" ? { subject_id: "", package_id: "" } : {}) }); };
  const openEdit = async (id) => {
    try {
      const { data: q } = await api.get(`/admin/questions/${id}`);
      setEdit(q);
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const del = async (id) => {
    try {
      await api.delete(`/admin/questions/${id}`);
      toast.success("Soal dihapus");
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  const smap = Object.fromEntries(subjects.map((s) => [s.id, s]));
  const subOpts = subjects.filter((s) => !flt.category_id || s.category_id === flt.category_id);
  const pages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;

  return (
    <div>
      <PageTitle title="Bank Soal" desc="Soal terpusat yang dapat dipakai ulang di beberapa paket tryout.">
        <button onClick={() => setImportOpen(true)} className="inline-flex items-center gap-2 rounded-lg bg-[#0A192F] px-4 py-2 text-sm font-semibold text-white" data-testid="questions-import-btn"><Upload className="h-4 w-4" />Impor CSV/Excel</button>
        <GoldBtn onClick={() => setEdit(newQ(flt.subject_id || subOpts[0]?.id))} disabled={!subjects.length} data-testid="questions-add-btn"><Plus className="h-4 w-4" />Tambah Soal</GoldBtn>
      </PageTitle>
      <div className="mb-4 grid gap-2 sm:grid-cols-3 lg:grid-cols-6">
        <div className="relative lg:col-span-2"><Search className="absolute left-3 top-3 h-4 w-4 text-slate-400" /><Input className="pl-9" placeholder="Cari teks soal..." value={flt.q} onChange={setF("q")} data-testid="questions-search-input" /></div>
        <NativeSelect value={flt.category_id} onChange={setF("category_id")} data-testid="questions-filter-category"><option value="">Semua kategori</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</NativeSelect>
        <NativeSelect value={flt.subject_id} onChange={setF("subject_id")} data-testid="questions-filter-subject"><option value="">Semua materi</option>{subOpts.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}</NativeSelect>
        <NativeSelect value={flt.difficulty} onChange={setF("difficulty")} data-testid="questions-filter-difficulty"><option value="">Semua tingkat</option><option value="mudah">Mudah</option><option value="sedang">Sedang</option><option value="sulit">Sulit</option></NativeSelect>
        <NativeSelect value={flt.package_id} onChange={setF("package_id")} data-testid="questions-filter-package"><option value="">Semua paket</option>{packages.filter((p) => !flt.category_id || p.category_id === flt.category_id).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}</NativeSelect>
      </div>
      {!data ? <Spinner /> : data.items.length === 0 ? <Empty text="Tidak ada soal sesuai filter." /> : (
        <>
          <div className="overflow-x-auto rounded-xl border bg-white">
            <table className="w-full min-w-[760px] text-sm" data-testid="questions-table">
              <thead className="bg-slate-50 text-left text-xs uppercase text-slate-500"><tr><th className="px-4 py-3">Pertanyaan</th><th>Materi</th><th>Tingkat</th><th>Kunci/Skor</th><th>Status</th><th className="pr-4 text-right">Aksi</th></tr></thead>
              <tbody>{data.items.map((q) => (
                <tr key={q.id} className="border-t" data-testid={`question-row-${q.id}`}>
                  <td className="max-w-md px-4 py-3"><span className="line-clamp-2">{q.text || "(soal gambar)"}</span>{q.image && <span className="text-[10px] font-bold text-amber-700">+ GAMBAR</span>}</td>
                  <td>{smap[q.subject_id]?.code || smap[q.subject_id]?.name}</td>
                  <td className="capitalize">{q.difficulty}</td>
                  <td className="font-mono text-xs">{q.answer_key || q.options.map((o) => o.score).join("/")}</td>
                  <td>{q.active ? "Aktif" : "Nonaktif"}</td>
                  <td className="pr-4 text-right">
                    <button onClick={() => openEdit(q.id)} className="mr-2" data-testid={`question-edit-${q.id}`}><Pencil className="h-4 w-4 text-slate-600" /></button>
                    <Confirm testid={`question-delete-${q.id}`} title="Hapus soal?" description="Soal akan dihapus dari bank soal dan seluruh paket tryout." onConfirm={() => del(q.id)}
                      trigger={<button data-testid={`question-delete-btn-${q.id}`}><Trash2 className="h-4 w-4 text-red-600" /></button>} />
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
          <div className="mt-4 flex items-center justify-between text-sm text-slate-600">
            <span data-testid="questions-total">Total {data.total} soal</span>
            <span className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="rounded border px-3 py-1 disabled:opacity-40" data-testid="questions-prev-page">‹</button>
              {page}/{pages}
              <button disabled={page >= pages} onClick={() => setPage(page + 1)} className="rounded border px-3 py-1 disabled:opacity-40" data-testid="questions-next-page">›</button>
            </span>
          </div>
        </>
      )}
      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent className="max-h-[92vh] max-w-3xl overflow-y-auto" data-testid="question-dialog">
          <DialogHeader><DialogTitle>{edit?.id ? "Edit soal" : "Tambah soal"}</DialogTitle></DialogHeader>
          {edit && <QuestionDialog q={edit} setQ={setEdit} subjects={subjects} packages={packages} onSaved={() => { setEdit(null); load(); }} />}
        </DialogContent>
      </Dialog>
      <Dialog open={importOpen} onOpenChange={setImportOpen}>
        <DialogContent data-testid="import-dialog">
          <DialogHeader><DialogTitle>Impor soal massal</DialogTitle></DialogHeader>
          <ImportDialog subjects={subjects} onDone={() => { setImportOpen(false); load(); }} />
        </DialogContent>
      </Dialog>
    </div>
  );
}
